// COMPONENT: ComparisonTableHomepage.tsx – generic 1‑Step filter (exclude Rapid), keep top‑5 (full file)
// -------------------------------------------------------------------------------------------------
// Fix: other firms' 1‑Step products had custom labels (e.g., "Express One‑Phase"). We now:
//   • Identify 1‑Step rows using `phases === 1` AND NOT label containing "rapid".
//   • Identify 2‑Step rows using `phases === 2` (label agnostic).
//   • Still restrict to $100,000 account size.
//   • Keeps special Prime‑CFD and Seacrest‑Two‑Step filters.
//   • After sorting by fee we slice to top‑5.

import React from "react";
import { getBestDiscount, type Discount, type Challenge } from "../utils/getBestDiscount";

/* ---------- helpers ---------------------------------------------------- */

/* ---------- types ------------------------------------------------------ */
interface Pricing {
  label?: string;
  phases: number;
  accountSize: number;
  fee: number;
  name?: string;
  maxDrawdownPercent?: number;
  maxDrawdown?: number;
  profitSplit?: number;
  dailyDrawdownPercent?: number;
  leverage?: string;
}

interface Firm {
  id: string;
  name: string;
  logo?: string;
  pricing: Pricing[];
  discounts?: Discount[];
  affiliateLink?: string;
  // Add extra properties here if needed, or use a safer type for dynamic keys:
  // [key: string]: unknown;
}

type RowType = Firm & {
  fee: number;
  discountedFee: number;
  accountSize: number;
  maxDD: string | number;
  dailyDD?: string | number;
  leverage?: string;
  isBestValue: boolean;
  discounts: Discount[];
  affiliateLink?: string;
  profitSplit?: string;
};

import type { PropFirm } from '../types';
import PromoCTAButtonHomepage from './PromoCTAButtonHomepage';

export interface ComparisonTableHomepageProps {
  firms: PropFirm[];
}

/* ---------- component -------------------------------------------------- */
const ComparisonTableHomepage: React.FC<ComparisonTableHomepageProps> = ({ firms }) => {
  const sections: (1 | 2)[] = [1, 2];

  // Normalize PropFirm[] to Firm[] for table logic
  const normalizedFirms: Firm[] = (firms
    .filter((firm) => firm.id !== 'blueguardian')
    .map((firm) => ({
      ...firm,
      pricing: Array.isArray(firm.pricing)
        ? firm.pricing
            .filter((p) => typeof p.accountSize === 'number' && typeof p.fee === 'number')
            .map((p) => ({
              ...p,
              phases: typeof p.phases === 'number' ? p.phases : (typeof p.phases === 'string' ? parseInt(p.phases, 10) || 0 : 0),
            }))
        : [],
    })) as Firm[]);

  return (
    <div className="space-y-12">
      {sections.map((type) => {
        const label = `${type}-Step Evaluation`;

        const rows: RowType[] = normalizedFirms
          .map((firm: Firm) => {
            const match = firm.pricing.filter((p: Pricing) => {
              const isRapid = p.label?.toLowerCase().includes("rapid");

              // 1‑Step: phases===1 and NOT Rapid
              if (type === 1 && (p.phases !== 1 || isRapid)) return false;
              // 2‑Step: phases===2
              if (type === 2 && p.phases !== 2) return false;

              if (p.accountSize !== 100000) return false;

              // firm‑specific filters
              const isPrimeCFD = firm.id === "ttp" && p.name?.toLowerCase().includes("prime cfd");
              if (firm.id === "ttp" && !isPrimeCFD) return false;

              const isSeacrestTwoStep =
                firm.id === "seacrest-funded" && p.name?.toLowerCase().includes("two-step challenge");
              if (firm.id === "seacrest-funded" && !isSeacrestTwoStep) return false;

              return true;
            });

            if (match.length === 0) return null;

            const best = match.reduce((prev: Pricing, curr: Pricing) => (curr.fee < prev.fee ? curr : prev));
            const discount = getBestDiscount(firm.discounts ?? [], best as Challenge);
            const hasDiscount = discount?.code && discount?.percent;
            const discountedFee = hasDiscount && discount.percent != null ? Math.round(best.fee * (1 - discount.percent / 100)) : best.fee;

            return {
  ...firm,
  fee: best.fee,
  discountedFee,
  accountSize: best.accountSize,
  maxDD:
    typeof best.maxDrawdownPercent === "number"
      ? best.maxDrawdownPercent
      : typeof best.maxDrawdown === "number"
      ? best.maxDrawdown
      : "-",
  dailyDD: typeof best.dailyDrawdownPercent === "number" ? best.dailyDrawdownPercent : "-",
  leverage: best.leverage || "-",
  isBestValue: false,
  discounts: discount ? [discount] : [],
  affiliateLink: firm.affiliateLink,
  // Per pricing.json guideline: profitSplit is a required number (percentage)
  profitSplit: typeof best.profitSplit === 'number' && !isNaN(best.profitSplit)
    ? `${best.profitSplit}%`
    : '-',
} as RowType;
          })
          .filter((x): x is RowType => x !== null)
          .sort((a, b) => a.fee - b.fee)
          .slice(0, 5);

        const bestRow = rows.find((r) => r && r.discounts?.[0]?.code && r.discounts?.[0]?.percent);
        if (bestRow) bestRow.isBestValue = true;

        return (
          <div key={label}>
            <h2 className="text-xl font-bold text-yellow-400 mb-4 border-b border-yellow-800 pb-2">{label}</h2>

            {/* mobile cards */}
            {/* Table Head for context */}
            <div className="hidden lg:block sticky top-[110px] z-20 bg-gray-900 pt-2 shadow-md shadow-black/30">
              <div className="max-w-6xl mx-auto px-4 overflow-x-auto bg-gray-900">
                <table className="min-w-full table-fixed text-yellow-300 text-xs lg:text-sm font-semibold">
                  <thead className="bg-gray-900">
                    <tr className="border-b border-yellow-900">
                      <th className="text-left px-2 py-3 lg:px-3 w-[22%]">Firm</th>
                      <th className="text-left px-2 py-3 lg:px-3 w-[13%]">Max DD</th>
                      <th className="text-left px-2 py-3 lg:px-3 w-[13%]">Daily DD</th>
                      <th className="text-left px-2 py-3 lg:px-3 w-[13%]">Leverage</th>
                      <th className="text-left px-2 py-3 lg:px-3 w-[12%]">Profit Split</th>
                      <th className="text-left px-2 py-3 lg:px-3 w-[12%]">Fee</th>
                      <th className="text-left px-2 py-3 lg:px-3 w-[15%]">Promo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row: RowType) => (
                      <tr key={row.id} className="border-b border-yellow-900 hover:bg-gray-900/90 transition">
                        {/* Firm */}
                        <td className="flex items-center gap-3 py-4 px-2 lg:px-3">
                          <a href={`/reviews/${row.id}`} className="w-[48px] flex items-center justify-start">
                            <img
                              src={row.logo || '/assets/logos/default-logo.svg'}
                              alt={`${row.name} logo`}
                              className="max-h-8 w-auto object-contain"
                            />
                          </a>
                          <div className="flex flex-col leading-tight max-w-[160px]">
                            <a href={`/reviews/${row.id}`} className="font-semibold text-yellow-300 hover:underline">
                              {row.name}
                            </a>
                            {row.isBestValue && (
                              <span className="text-xs text-yellow-300 bg-yellow-700/20 rounded px-2 py-0.5 w-max mt-1">
                                🔥 Best Deal
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Max DD */}
                        <td className="text-sm py-4 px-2 lg:px-3">{typeof row.maxDD === 'number' ? `${row.maxDD}%` : row.maxDD}</td>
                        {/* Daily DD */}
                        <td className="text-sm py-4 px-2 lg:px-3">{typeof row.dailyDD === 'number' ? `${row.dailyDD}%` : row.dailyDD || '-'}</td>
                        {/* Leverage */}
                        <td className="text-sm py-4 px-2 lg:px-3">{row.leverage || '-'}</td>
                        {/* Profit Split */}
                        <td className="text-sm py-4 px-2 lg:px-3">{row.profitSplit || '-'}</td>
                        {/* Fee */}
                        <td className="text-sm py-4 px-2 lg:px-3" aria-label={`Fee: ${row.discountedFee < row.fee ? `${row.fee} discounted to ${row.discountedFee}` : row.fee}`}>
                          {row.discountedFee < row.fee ? (
                            <>
                              <s className="text-gray-500">${row.fee}</s>{' '}
                              <span className="text-yellow-300 font-bold">${row.discountedFee}</span>
                            </>
                          ) : (
                            <span className="text-yellow-300 font-bold">${row.fee}</span>
                          )}
                        </td>
                        {/* Promo CTA */}
                        <td className="py-4 px-2 lg:px-3">
                          <PromoCTAButtonHomepage firm={{
                            id: row.id,
                            affiliateLink: row.affiliateLink,
                            fee: row.fee,
                            discounts: row.discounts
                          }} isBestValue={row.isBestValue} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ComparisonTableHomepage;
